import React, { useState, useEffect, useMemo } from 'react';
import api from '../../lib/api';
import AdminLayout from '@/components/Layout';
import { Package, Plus, Trash2, Pencil, X, Search, CheckSquare, Square, Users } from 'lucide-react';

const EMPTY = { name: '', channel_ids: [], is_active: true };

export default function Bouquets() {
  const [bouquets, setBouquets] = useState([]);
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [channelQuery, setChannelQuery] = useState('');
  const [assignBouquet, setAssignBouquet] = useState(null);

  const load = async () => {
    try {
      const [bRes, cRes, catRes] = await Promise.all([
        api.get('/bouquets'),
        api.get('/channels'),
        api.get('/categories'),
      ]);
      setBouquets(bRes.data);
      setChannels(cRes.data);
      setCategories(catRes.data);
    } catch (err) {
      console.error('Bouquet/kanal yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const channelMap = useMemo(() => {
    const map = new Map();
    channels.forEach(c => map.set(c._id, c));
    return map;
  }, [channels]);

  const categoryName = (id) => categories.find(c => c._id === id)?.name || 'Kategorisiz';

  const filteredChannels = useMemo(() => {
    const q = channelQuery.trim().toLowerCase();
    if (!q) return channels;
    return channels.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      categoryName(c.category_id)?.toLowerCase().includes(q)
    );
    // categoryName depends on categories which is in deps below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, channelQuery, categories]);

  const openCreate = async () => {
    // Veri tutarlılığı: modal açılmadan önce kanal listesi taze olsun
    try {
      const cRes = await api.get('/channels');
      setChannels(cRes.data);
    } catch {}
    setEditing(null);
    setForm(EMPTY);
    setChannelQuery('');
    setShowModal(true);
  };

  const openEdit = async (b) => {
    // Refresh channels so deleted channels don't appear as still-selected
    try {
      const cRes = await api.get('/channels');
      setChannels(cRes.data);
      const valid = new Set(cRes.data.map(c => c._id));
      setForm({
        name: b.name,
        channel_ids: (b.channel_ids || []).filter(id => valid.has(id)),
        is_active: b.is_active,
      });
    } catch {
      setForm({ name: b.name, channel_ids: b.channel_ids || [], is_active: b.is_active });
    }
    setEditing(b);
    setChannelQuery('');
    setShowModal(true);
  };

  const toggleChannel = (id) => {
    setForm(f => ({
      ...f,
      channel_ids: f.channel_ids.includes(id)
        ? f.channel_ids.filter(x => x !== id)
        : [...f.channel_ids, id],
    }));
  };

  const selectAllVisible = () => {
    const visibleIds = filteredChannels.map(c => c._id);
    setForm(f => ({ ...f, channel_ids: Array.from(new Set([...f.channel_ids, ...visibleIds])) }));
  };

  const clearVisible = () => {
    const visibleIds = new Set(filteredChannels.map(c => c._id));
    setForm(f => ({ ...f, channel_ids: f.channel_ids.filter(id => !visibleIds.has(id)) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/bouquets/${editing._id}`, form);
      } else {
        await api.post('/bouquets', form);
      }
      setShowModal(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      alert(editing ? 'Bouquet güncellenemedi.' : 'Bouquet eklenemedi.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu paketi silmek istediğinize emin misiniz? Kanallar etkilenmez.')) return;
    try {
      await api.delete(`/bouquets/${id}`);
      setBouquets(bouquets.filter(b => b._id !== id));
    } catch (err) {
      alert('Silinemedi.');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="bouquets-page-title">
            <Package className="text-amber-500" /> Buketler (Paketler)
          </h1>
          <button
            onClick={openCreate}
            className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
            data-testid="bouquets-add-btn"
          >
            <Plus size={20} /> Yeni Paket
          </button>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
          <table className="w-full text-left text-white" data-testid="bouquets-table">
            <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold tracking-wider">
              <tr>
                <th className="p-4 font-semibold text-white">Paket Adı</th>
                <th className="p-4 font-semibold text-white text-right">Kanal Sayısı</th>
                <th className="p-4 font-semibold text-white">Önizleme</th>
                <th className="p-4 font-semibold text-white">Durum</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan="5" className="p-12 text-center text-gray-500 italic">Yükleniyor…</td></tr>
              ) : bouquets.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-12 text-center text-gray-500 italic" data-testid="bouquets-empty">
                    Henüz paket yok. "Yeni Paket" butonunu kullanın.
                  </td>
                </tr>
              ) : (
                bouquets.map(b => {
                  const liveChannels = (b.channel_ids || []).filter(id => channelMap.has(id));
                  const preview = liveChannels.slice(0, 3).map(id => channelMap.get(id).name).join(', ');
                  const remaining = liveChannels.length - 3;
                  return (
                    <tr key={b._id} className="hover:bg-slate-800/30 transition-colors group" data-testid={`bouquet-row-${b._id}`}>
                      <td className="p-4 font-bold text-white">{b.name}</td>
                      <td className="p-4 text-right">
                        <span className="font-mono text-amber-400 text-sm">{liveChannels.length}</span>
                      </td>
                      <td className="p-4 text-gray-400 text-xs truncate max-w-[420px]">
                        {liveChannels.length === 0 ? <span className="italic text-gray-600">Boş paket</span> : (
                          <>
                            {preview}
                            {remaining > 0 && <span className="text-gray-500"> +{remaining} kanal</span>}
                          </>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`flex items-center gap-1.5 text-[10px] font-bold uppercase ${b.is_active ? 'text-green-500' : 'text-gray-500'}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${b.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
                          {b.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setAssignBouquet(b)}
                            className="text-gray-500 hover:text-emerald-400 hover:bg-emerald-500/10 p-2 rounded-lg transition-all"
                            data-testid={`bouquet-assign-btn-${b._id}`}
                            title="Müşteriye Ata"
                          >
                            <Users size={16} />
                          </button>
                          <button
                            onClick={() => openEdit(b)}
                            className="text-gray-500 hover:text-amber-500 hover:bg-amber-500/10 p-2 rounded-lg transition-all"
                            data-testid={`bouquet-edit-btn-${b._id}`}
                            title="Düzenle"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(b._id)}
                            className="text-gray-500 hover:text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-all"
                            data-testid={`bouquet-delete-btn-${b._id}`}
                            title="Sil"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4" data-testid="bouquets-modal">
            <div className="bg-slate-900 border border-slate-800 w-full max-w-3xl rounded-2xl p-8 shadow-2xl flex flex-col max-h-[90vh]">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Package className="text-amber-500" /> {editing ? 'Paketi Düzenle' : 'Yeni Paket'}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white" data-testid="bouquets-modal-close">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4 flex-1 overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1">Paket Adı</label>
                    <input
                      required
                      placeholder="Örn: Premium Spor Paketi"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      data-testid="bouquet-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1">Aktif</label>
                    <label className="flex items-center justify-center h-[52px] px-4 bg-slate-950 border border-slate-800 rounded-xl cursor-pointer hover:border-amber-500/40 transition">
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={e => setForm({ ...form, is_active: e.target.checked })}
                        className="w-5 h-5 accent-amber-500"
                        data-testid="bouquet-active-checkbox"
                      />
                    </label>
                  </div>
                </div>

                <div className="space-y-2 flex flex-col flex-1 overflow-hidden">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                      Kanallar <span className="text-amber-500 normal-case">({form.channel_ids.length} seçili / {channels.length} toplam)</span>
                    </label>
                    <div className="flex gap-2">
                      <button type="button" onClick={selectAllVisible} className="text-xs text-amber-500 hover:text-amber-400 font-bold uppercase tracking-wide" data-testid="bouquet-select-all-btn">
                        Tümünü Seç
                      </button>
                      <span className="text-gray-700">|</span>
                      <button type="button" onClick={clearVisible} className="text-xs text-gray-400 hover:text-red-400 font-bold uppercase tracking-wide" data-testid="bouquet-clear-btn">
                        Temizle
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Kanal ara (ad veya kategori)..."
                      className="w-full pl-11 bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none transition-all text-sm"
                      value={channelQuery}
                      onChange={e => setChannelQuery(e.target.value)}
                      data-testid="bouquet-channel-search"
                    />
                  </div>

                  <div className="flex-1 overflow-y-auto bg-slate-950 border border-slate-800 rounded-xl divide-y divide-slate-800/60 min-h-[240px]" data-testid="bouquet-channel-list">
                    {channels.length === 0 ? (
                      <div className="p-8 text-center text-gray-500 italic text-sm">
                        Henüz kanal yok. Önce <span className="text-amber-500">/admin/channels</span> sayfasından kanal ekleyin.
                      </div>
                    ) : filteredChannels.length === 0 ? (
                      <div className="p-8 text-center text-gray-500 italic text-sm">
                        "{channelQuery}" için sonuç yok.
                      </div>
                    ) : (
                      filteredChannels.map(ch => {
                        const checked = form.channel_ids.includes(ch._id);
                        return (
                          <button
                            type="button"
                            key={ch._id}
                            onClick={() => toggleChannel(ch._id)}
                            className={`w-full flex items-center gap-3 p-3 text-left hover:bg-slate-800/40 transition ${checked ? 'bg-amber-500/5' : ''}`}
                            data-testid={`bouquet-channel-${ch._id}`}
                          >
                            {checked
                              ? <CheckSquare size={18} className="text-amber-500 shrink-0" />
                              : <Square size={18} className="text-gray-600 shrink-0" />}
                            <div className="w-8 h-8 bg-slate-900 border border-slate-800 rounded flex items-center justify-center overflow-hidden shrink-0">
                              {ch.logo_url
                                ? <img src={ch.logo_url} alt="" className="w-full h-full object-contain" />
                                : <span className="text-[9px] text-gray-600 font-mono">{ch.name?.slice(0, 2).toUpperCase()}</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-white font-medium truncate">{ch.name}</div>
                              <div className="text-[10px] font-mono text-gray-500 truncate">
                                {categoryName(ch.category_id)} · {ch.status || '—'}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="flex gap-4 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-slate-800 text-white font-bold py-4 rounded-xl hover:bg-slate-700 transition-colors"
                    data-testid="bouquet-cancel-btn"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-amber-500 text-black font-bold py-4 rounded-xl hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20"
                    data-testid="bouquet-save-btn"
                  >
                    {editing ? 'Güncelle' : 'Kaydet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <AssignToCustomersModal
          bouquet={assignBouquet}
          onClose={() => setAssignBouquet(null)}
        />
      </div>
    </AdminLayout>
  );
}

function AssignToCustomersModal({ bouquet, onClose }) {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!bouquet) return;
    setLoading(true);
    setQuery('');
    api.get('/customers')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        setCustomers(list);
        // Pre-check müşterileri: zaten bu bouquet'i taşıyanlar.
        const pre = new Set(
          list.filter(c => (c.bouquet_ids || []).includes(bouquet._id)).map(c => c._id)
        );
        setSelected(pre);
      })
      .catch(err => console.error('Müşteri yükleme hatası:', err))
      .finally(() => setLoading(false));
  }, [bouquet]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c =>
      c.full_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  }, [customers, query]);

  if (!bouquet) return null;

  const toggle = (id) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const tasks = customers.map(c => {
        const had = (c.bouquet_ids || []).includes(bouquet._id);
        const want = selected.has(c._id);
        if (had === want) return null;
        const next = want
          ? Array.from(new Set([...(c.bouquet_ids || []), bouquet._id]))
          : (c.bouquet_ids || []).filter(b => b !== bouquet._id);
        return api.patch(`/customers/${c._id}/bouquets`, { bouquet_ids: next });
      }).filter(Boolean);
      await Promise.all(tasks);
      onClose();
    } catch (err) {
      alert('Atama sırasında bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4" data-testid="bouquet-assign-modal">
      <div className="bg-slate-900 border border-slate-800 w-full max-w-2xl rounded-2xl p-8 shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center mb-1">
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="text-emerald-400" /> Müşteriye Ata
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="bouquet-assign-modal-close">
            <X size={20} />
          </button>
        </div>
        <div className="text-sm text-gray-400 mb-5">
          Paket: <span className="text-amber-400 font-bold">{bouquet.name}</span>
        </div>

        <div className="relative mb-3">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Müşteri ara (ad veya e-posta)..."
            className="w-full pl-11 bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-emerald-500 outline-none transition-all text-sm"
            value={query}
            onChange={e => setQuery(e.target.value)}
            data-testid="bouquet-assign-search"
          />
        </div>

        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
            <span className="text-emerald-400">{selected.size}</span> seçili / {customers.length} müşteri
          </span>
          <div className="flex gap-3 text-xs font-bold uppercase tracking-wide">
            <button
              type="button"
              onClick={() => setSelected(new Set(filtered.map(c => c._id)))}
              className="text-emerald-400 hover:text-emerald-300"
              data-testid="bouquet-assign-select-all-btn"
            >
              Tümünü Seç
            </button>
            <span className="text-gray-700">|</span>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-gray-400 hover:text-red-400"
              data-testid="bouquet-assign-clear-btn"
            >
              Temizle
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-950 border border-slate-800 rounded-xl divide-y divide-slate-800/60 min-h-[280px]" data-testid="bouquet-assign-customer-list">
          {loading ? (
            <div className="p-8 text-center text-gray-500 italic text-sm">Yükleniyor…</div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center text-gray-500 italic text-sm">
              Henüz müşteri yok. Önce <span className="text-amber-500">/admin/customers</span> sayfasından müşteri ekleyin.
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500 italic text-sm">"{query}" için sonuç yok.</div>
          ) : (
            filtered.map(c => {
              const checked = selected.has(c._id);
              const hadAlready = (c.bouquet_ids || []).includes(bouquet._id);
              return (
                <button
                  type="button"
                  key={c._id}
                  onClick={() => toggle(c._id)}
                  className={`w-full flex items-center gap-3 p-3 text-left hover:bg-slate-800/40 transition ${checked ? 'bg-emerald-500/5' : ''}`}
                  data-testid={`bouquet-assign-customer-${c._id}`}
                >
                  {checked
                    ? <CheckSquare size={18} className="text-emerald-400 shrink-0" />
                    : <Square size={18} className="text-gray-600 shrink-0" />}
                  <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center text-amber-500 font-bold text-sm shrink-0">
                    {(c.full_name || '—').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate flex items-center gap-2">
                      {c.full_name}
                      {hadAlready && (
                        <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-700/30">
                          Atanmış
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] font-mono text-gray-500 truncate">{c.email}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-gray-700 text-gray-400'}`}>
                    {c.status}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="flex gap-4 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-slate-800 text-white font-bold py-4 rounded-xl hover:bg-slate-700 transition-colors"
            data-testid="bouquet-assign-cancel-btn"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-emerald-500 text-black font-bold py-4 rounded-xl hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/20 disabled:opacity-60"
            data-testid="bouquet-assign-save-btn"
          >
            {saving ? 'Atanıyor…' : `Atama Yap (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

